import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Car, User, FileText, Loader2, Eye, ShoppingCart } from 'lucide-react';
import { DocumentsSection } from '@/components/DocumentsSection';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const statusLabels = { pending: 'In asteptare', in_processing: 'In procesare', completed: 'Finalizat', rejected: 'Respins' };
const statusColors = { pending: 'bg-amber-50 text-amber-700', in_processing: 'bg-blue-50 text-blue-700', completed: 'bg-emerald-50 text-emerald-700', rejected: 'bg-red-50 text-red-700' };

export default function AdminVehicleDetail() {
  const { vehicleId } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);

  useEffect(() => { fetchVehicle(); fetchServices(); }, [vehicleId]);

  const fetchVehicle = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(API + '/admin/vehicles/' + vehicleId, { headers: { Authorization: 'Bearer ' + token }, withCredentials: true });
      setVehicle(response.data);
    } catch (error) { toast.error('Eroare'); } finally { setLoading(false); }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(API + '/services');
      setServices(response.data);
    } catch (error) {}
  };

  const servicesMap = {};
  services.forEach(s => { servicesMap[s.service_id] = s.name; });

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  if (!vehicle) return <div className="text-center py-12">Vehicul negasit</div>;

  const owner = vehicle.client;
  const orders = vehicle.orders || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/admin/vehicles"><Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-2" />Inapoi</Button></Link>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{vehicle.brand} {vehicle.model}</h1>
          <p className="text-sm text-slate-500">VIN: {vehicle.vin}</p>
        </div>
        {vehicle.plate_number && <Badge variant="secondary" className="text-lg px-3 py-1">{vehicle.plate_number}</Badge>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Vehicle Info */}
        <Card className="bg-white lg:col-span-2">
          <CardHeader><CardTitle className="flex items-center gap-2"><Car className="h-5 w-5 text-blue-600" />Date vehicul</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <div><p className="text-sm text-slate-500">Marca</p><p className="font-medium">{vehicle.brand}</p></div>
            <div><p className="text-sm text-slate-500">Model</p><p className="font-medium">{vehicle.model || 'N/A'}</p></div>
            <div><p className="text-sm text-slate-500">VIN</p><p className="font-medium font-mono text-sm">{vehicle.vin}</p></div>
            <div><p className="text-sm text-slate-500">Nr. inmatriculare</p><p className="font-medium">{vehicle.plate_number || 'N/A'}</p></div>
            <div><p className="text-sm text-slate-500">An fabricatie</p><p className="font-medium">{vehicle.year || 'N/A'}</p></div>
            <div><p className="text-sm text-slate-500">Capacitate cilindrica</p><p className="font-medium">{vehicle.capacitate_cilindrica ? `${vehicle.capacitate_cilindrica} cc` : 'N/A'}</p></div>
            <div><p className="text-sm text-slate-500">Serie motor</p><p className="font-medium">{vehicle.serie_motor || 'N/A'}</p></div>
            {vehicle.data_dobandire && <div><p className="text-sm text-slate-500">Data dobandire</p><p className="font-medium">{vehicle.data_dobandire}</p></div>}
          </CardContent>
        </Card>

        {/* Owner Card */}
        <Card className="bg-white">
          <CardHeader><CardTitle className="flex items-center gap-2"><User className="h-5 w-5 text-blue-600" />Proprietar</CardTitle></CardHeader>
          <CardContent>
            {owner ? (
              <div className="space-y-3">
                <div>
                  <p className="font-semibold text-lg">{owner.name}</p>
                  <Badge variant="secondary" className="mt-1">{owner.client_type === 'persoana_juridica' ? 'PJ' : 'PF'}</Badge>
                </div>
                <div><p className="text-sm text-slate-500">CNP/CUI</p><p className="text-sm">{owner.cnp_cui}</p></div>
                {owner.email && <div><p className="text-sm text-slate-500">Email</p><p className="text-sm">{owner.email}</p></div>}
                {owner.phone && <div><p className="text-sm text-slate-500">Telefon</p><p className="text-sm">{owner.phone}</p></div>}
                {owner.address && <div><p className="text-sm text-slate-500">Adresa</p><p className="text-sm">{owner.address}</p></div>}
                <Link to={`/admin/clients/${owner.client_id}`}>
                  <Button className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white" data-testid="go-to-owner-btn">
                    <User className="h-4 w-4 mr-2" />
                    Pagina proprietar
                  </Button>
                </Link>
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">Proprietar necunoscut</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Documents */}
      <Card className="bg-white">
        <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-blue-600" />Documente vehicul</CardTitle></CardHeader>
        <CardContent>
          <DocumentsSection documents={vehicle.documents || []} uploadUrl={`/admin/vehicles/${vehicleId}/documents`} onRefresh={fetchVehicle} title="Documente" />
        </CardContent>
      </Card>

      {/* Orders for this vehicle */}
      <Card className="bg-white">
        <CardHeader><CardTitle className="flex items-center gap-2"><ShoppingCart className="h-5 w-5 text-blue-600" />Comenzi ({orders.length})</CardTitle></CardHeader>
        <CardContent>
          {orders.length === 0 ? <p className="text-sm text-slate-500 text-center py-4">Nicio comanda pentru acest vehicul</p> : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left px-4 py-2 text-sm font-medium text-slate-600">Nr. Comanda</th>
                    <th className="text-left px-4 py-2 text-sm font-medium text-slate-600">Data</th>
                    <th className="text-left px-4 py-2 text-sm font-medium text-slate-600">Servicii</th>
                    <th className="text-right px-4 py-2 text-sm font-medium text-slate-600">Total</th>
                    <th className="text-center px-4 py-2 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-center px-4 py-2 text-sm font-medium text-slate-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => {
                    const srvNames = [];
                    (o.vehicles || []).forEach(v => (v.services || []).forEach(s => {
                      const sid = typeof s === 'string' ? s : s.service_id;
                      const name = servicesMap[sid] || sid;
                      if (!srvNames.includes(name)) srvNames.push(name);
                    }));
                    return (
                      <tr key={o.order_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3"><span className="font-medium text-sm">{o.order_number}</span></td>
                        <td className="px-4 py-3 text-sm text-slate-500">{new Date(o.created_at).toLocaleDateString('ro-RO')}</td>
                        <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{srvNames.map(s => <Badge key={s} variant="outline" className="text-xs">{s}</Badge>)}</div></td>
                        <td className="px-4 py-3 text-sm text-right font-semibold">{o.total_price} EUR</td>
                        <td className="px-4 py-3 text-center"><Badge className={`${statusColors[o.status] || 'bg-slate-100'} text-xs`}>{statusLabels[o.status] || o.status}</Badge></td>
                        <td className="px-4 py-3 text-center"><Link to={`/admin/orders/${o.order_id}`}><Button variant="ghost" size="sm" className="h-8 w-8 p-0"><Eye className="h-4 w-4" /></Button></Link></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
